import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { environment } from "../../../../environments/environment";
import { tripSqi } from "../models/tripSqi";
import { Observable } from "rxjs";
import { TripSearchParams } from "../models/tripSearchParams";
import { map } from "rxjs/operators";
import { TripSlas } from "../models/tripSlas";
import { DbKeyValueDecimal } from "../models/dbKeyValueDecimal";
import { TelecomOperator } from "../models/telecomOperator";
import { TripStats } from "../models/tripStats";
import { Trip } from "../models/trip-overview";
import { ResponseText} from "../../../shared/models/shared.model";

@Injectable({
  providedIn: "root",
})
export class ReportingDataService {
  constructor(private http: HttpClient) {}

  getOperators = (): Observable<TelecomOperator[]> => {
    return this.http.get<TelecomOperator[]>(
      `${environment.api.baseUrl}/${environment.api.endpoints.telecomOperators}`
    );
  };

  getAdvancedQuerySQIs = (projectId: number): Observable<tripSqi[]> => {
    return this.http.get<tripSqi[]>(
      `${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.SQIs}?projectId=${projectId}`
    );
  };

  getDashboardTrips = (
    projectId: number,
    searchParams: TripSearchParams
  ): Observable<number[]> => {
    const params = new HttpParams()
      .set("projectId", projectId.toString())
      .set("query", searchParams.query || '')
      .set("trainIds", searchParams.trainIds)
      .set("from", searchParams.from)
      .set("to", searchParams.to)
      .set("type", searchParams.type);

    return this.http
      .get<{ tripIds: number[] }>(
        `${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.trips}/dashboard/ids`,
        {
          params,
        }
      )
      .pipe(map((data) => data.tripIds));
  };

  getTripSlas = (
    projectId: number,
    tripIds: number[]
  ): Observable<TripSlas> => {
    return this.http.post<TripSlas>(
      `${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.trips}/dashboard/${projectId}/slas`,
      {
        tripIds,
      }
    );
  };

  getTripDashboardStats = (
    projectId: number,
    tripIds: number[],
    endPoint: string
  ): Observable<DbKeyValueDecimal[]> => {
    return this.http.post<DbKeyValueDecimal[]>(
      `${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.trips}/dashboard/${projectId}/${endPoint}`,
      {
        tripIds,
      }
    );
  };

  exportTrips = (
    projectId: number,
    searchParams: TripSearchParams
  ): Promise<TripStats[]> => {
    const params = new HttpParams()
      .set("projectId", projectId.toString())
      .set("query", searchParams.query)
      .set("trainIds", searchParams.trainIds)
      .set("from", searchParams.from)
      .set("to", searchParams.to)
      .set("type", searchParams.type);

    return this.http
      .get<TripStats[]>(
        `${environment.api.baseUrl}/${environment.api.endpoints.export.dashboardTrips}`,
        {
          params,
        }
      ).toPromise();
  };

  getTrips = (projectId: number, from: string, to: string, trains: string, query: string): Observable<Trip[]> => {

    const params = new HttpParams()
        .set('projectId', projectId.toString())
        .set('from', from)
        .set('to', to)
        .set('type', 'main')
        .set('query', query)
        .set('trainIds', trains);

    return this.http.get<Trip[]>(`${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.trips}`, {
      params
    });
  }

  getTripSlaDocumentation = (projectId: number, fileName: string): any => {
      const params = new HttpParams()
          .set('projectId', projectId.toString())
          .set('documentationFile', fileName);

      return this.http.get(`${environment.api.baseUrl}/${environment.api.endpoints.static.doc}`, {
              params,
              responseType: 'text'
          }).toPromise();
  }

  recalculateTrips = (projectId: number, tripIds: string): Promise<ResponseText> => {
      const params = new HttpParams()
          .set('projectId', projectId.toString())
          .set('tripIds', tripIds);

      return this.http.post<ResponseText>(`${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.recalculateTrips}`, {}, {
          params
      }).toPromise();

  }

  recalculateTripsByTrainIds = (projectId: number, from: string, to: string, trainIds: string): Promise<ResponseText> => {
      const params = new HttpParams()
          .set('projectId', projectId.toString())
          .set('from', from)
          .set('to', to)
          .set('trainIds', trainIds);

      return this.http.post<ResponseText>(`${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.recalculateTripsByTrainIds}`, {}, {
          params
      }).toPromise();
  }

  calculateTripsBySchedule = (projectId: number, from: string, to: string, trainIds: string): Promise<ResponseText> => {
      const params = new HttpParams()
          .set('projectId', projectId.toString())
          .set('from', from)
          .set('to', to)
          .set('trainIds', trainIds);

      return this.http.post<ResponseText>(`${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.calculateTripsBySchedule}`, {}, {
          params
      }).toPromise();
  }

  fetchTrainSchedule = (projectId: number, date: string, trainIds: string): Promise<ResponseText> => {
      const params = new HttpParams()
          .set('projectId', projectId.toString())
          .set('date', date)
          .set('trainIds', trainIds);

      return this.http.post<ResponseText>(`${environment.api.baseUrl}/${environment.api.endpoints.advancedQuery.fetchTrainSchedule}`, {}, {
          params
      }).toPromise();

  }
}